import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, Building2, CreditCard, Landmark, Plus, Trash2 } from 'lucide-react';
import { formatMoney } from '@erp/shared';
import { aplicarIban, rellenadoDeGuardado } from '@/lib/cuenta-banco';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, ErrorState, EmptyState, StatCard } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useConfirm } from '@/components/ui/use-confirm';
import { Field, Input, Select, Textarea } from '@/components/ui/input';
import { toast } from 'sonner';

/**
 * Tesorería: las cuentas de la empresa, las formas de pago y el dinero que entra y sale. Los datos
 * fiscales de la empresa no pintan aquí: son Administración → Datos de la empresa.
 *
 * El saldo de cada cuenta lo calcula el servidor (apertura + movimientos): aquí no se suma nada a
 * mano, porque una pantalla que suma por su cuenta acaba diciendo otra cosa que la del al lado.
 */

interface Cuenta {
  id: string; alias: string; bankName: string | null; iban: string; swift?: string | null;
  notes?: string | null; currency: string;
  openingBalance: number; saldo: number; movimientos: number; active: boolean;
  isDefaultCollections: boolean; isDefaultPayments: boolean;
}

interface Movimiento {
  id: string; account_id: string; account_alias: string; amount: string; booked_at: string;
  concept: string; reference: string | null; method_name: string | null; source: string;
  supplier_name: string | null; customer_name: string | null;
}

interface FormaPago {
  id: string; code: string; name: string; kind: string; appliesTo: 'cobros' | 'pagos' | 'ambos';
  days: number; requiresIban: boolean; bankAccountId: string | null; active: boolean; sortOrder: number;
}


const TIPOS: { value: string; label: string }[] = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'domiciliacion', label: 'Domiciliación' },
  { value: 'pagare', label: 'Pagaré' },
  { value: 'confirming', label: 'Confirming' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'otro', label: 'Otro' }
];

const cuentaVacia = {
  alias: '', bankName: '', iban: '', swift: '', currency: 'EUR', openingBalance: '0',
  isDefaultCollections: false, isDefaultPayments: false, notes: '', active: true
};
const movVacio = { accountId: '', amount: '', bookedAt: new Date().toISOString().slice(0, 10), concept: '', methodId: '', reference: '' };
const formaVacia = { code: '', name: '', kind: 'transferencia', appliesTo: 'ambos', days: '0', requiresIban: false, bankAccountId: '', sortOrder: '0', active: true };

export function TreasuryPage() {
  const { can } = useAuth();
  const puedeEditar = can('finance.write');
  const qc = useQueryClient();
  const { confirm, dialog: dialogoConfirmacion } = useConfirm();
  const [tab, setTab] = useState('resumen');

  const resumen = useQuery({
    queryKey: ['treasury', 'summary'],
    queryFn: () => api<{
      cuentas: Cuenta[]; saldoTotal: number;
      pendientePago: { total: number; facturas: number; vencidas: number };
      movimientos: Movimiento[]; formasPago: FormaPago[];
    }>('/treasury/summary')
  });
  const cuentas = useQuery({
    queryKey: ['treasury', 'accounts'],
    queryFn: () => api<{ data: Cuenta[] }>('/bank-accounts?all=true')
  });
  // Solo quien gestiona las formas de pago puede ver las inactivas (Dirección tiene lectura y
  // recibía 403 con la lista vacía y sin ningún aviso).
  const puedeGestionar = puedeEditar;
  const formas = useQuery({
    queryKey: ['treasury', 'methods', puedeGestionar],
    queryFn: () => api<{ data: FormaPago[] }>(
      `/payment-methods${puedeGestionar ? '?includeInactive=true' : ''}`
    )
  });

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['treasury'] });
    void qc.invalidateQueries({ queryKey: ['payment-methods'] });
  };

  const [formCuenta, setFormCuenta] = useState<typeof cuentaVacia | null>(null);
  // Lo que puso el autocompletado (o lo que venía guardado al editar): al cambiar de IBAN se sustituye
  // solo eso, nunca lo que haya escrito el usuario.
  const [rellenado, setRellenado] = useState({ bankName: '', swift: '', entidad: '' });
  const [editandoCuenta, setEditandoCuenta] = useState<string | null>(null);
  const [formMov, setFormMov] = useState<typeof movVacio | null>(null);
  const [formForma, setFormForma] = useState<typeof formaVacia | null>(null);
  const [editandoForma, setEditandoForma] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const listaCuentas = cuentas.data?.data ?? [];
  const listaFormas = formas.data?.data ?? [];
  const activas = useMemo(() => listaCuentas.filter((cuenta) => cuenta.active), [listaCuentas]);

  const guardarCuenta = async () => {
    if (!formCuenta) return;
    setGuardando(true);
    try {
      const cuerpo = {
        alias: formCuenta.alias,
        bankName: formCuenta.bankName || null,
        iban: formCuenta.iban,
        swift: formCuenta.swift || null,
        currency: formCuenta.currency,
        openingBalance: Number(formCuenta.openingBalance.replace(',', '.')) || 0,
        isDefaultCollections: formCuenta.isDefaultCollections,
        isDefaultPayments: formCuenta.isDefaultPayments,
        notes: formCuenta.notes || null,
        active: formCuenta.active
      };
      if (editandoCuenta) await api(`/bank-accounts/${editandoCuenta}`, { method: 'PATCH', body: cuerpo });
      else await api('/bank-accounts', { method: 'POST', body: cuerpo });
      toast.success(editandoCuenta ? 'Cuenta actualizada' : 'Cuenta creada');
      setFormCuenta(null); setEditandoCuenta(null); refrescar();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo guardar la cuenta');
    } finally { setGuardando(false); }
  };

  const guardarMovimiento = async () => {
    if (!formMov) return;
    setGuardando(true);
    try {
      // El signo es lo que dice si entra o sale: en la pantalla se escribe el importe tal cual, con
      // el menos delante cuando es un pago.
      await api('/bank-movements', {
        method: 'POST',
        body: {
          accountId: formMov.accountId,
          amount: Number(formMov.amount.replace(',', '.')),
          bookedAt: formMov.bookedAt,
          concept: formMov.concept,
          methodId: formMov.methodId || null,
          reference: formMov.reference || null
        }
      });
      toast.success('Movimiento registrado');
      setFormMov(null); refrescar();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo registrar el movimiento');
    } finally { setGuardando(false); }
  };

  const guardarForma = async () => {
    if (!formForma) return;
    setGuardando(true);
    try {
      const cuerpo = {
        code: formForma.code.toUpperCase(),
        name: formForma.name,
        kind: formForma.kind,
        appliesTo: formForma.appliesTo,
        days: Number(formForma.days) || 0,
        requiresIban: formForma.requiresIban,
        bankAccountId: formForma.bankAccountId || null,
        sortOrder: Number(formForma.sortOrder) || 0,
        active: formForma.active
      };
      if (editandoForma) await api(`/payment-methods/${editandoForma}`, { method: 'PATCH', body: cuerpo });
      else await api('/payment-methods', { method: 'POST', body: cuerpo });
      toast.success(editandoForma ? 'Forma de pago actualizada' : 'Forma de pago creada');
      setFormForma(null); setEditandoForma(null); refrescar();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo guardar la forma de pago');
    } finally { setGuardando(false); }
  };

  const borrarMovimiento = async (id: string) => {
    // Borra dinero de un listado: se pregunta antes, como el resto de acciones que no se deshacen.
    const sigue = await confirm({
      title: '¿Borrar el movimiento?',
      description: 'Se quitará del saldo de la cuenta. No se puede deshacer.',
      confirmLabel: 'Borrar movimiento',
      destructive: true
    });
    if (!sigue) return;
    try {
      await api(`/bank-movements/${id}`, { method: 'DELETE' });
      toast.success('Movimiento borrado'); refrescar();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo borrar');
    }
  };

  const columnasCuentas: Column<Cuenta>[] = [
    { key: 'alias', header: 'Cuenta', cell: (fila) => (
      <div>
        <p className="font-medium">{fila.alias} {fila.isDefaultCollections ? <Badge tone="info">Cobros</Badge> : null} {fila.isDefaultPayments ? <Badge tone="info">Pagos</Badge> : null}</p>
        <p className="font-mono text-[11px] text-muted-foreground">{fila.iban}</p>
      </div>
    ) },
    { key: 'bankName', header: 'Banco', cell: (fila) => fila.bankName ?? '—' },
    { key: 'saldo', header: 'Saldo', headerClassName: 'text-right', className: 'text-right', cell: (fila) => (
      <span className={fila.saldo < 0 ? 'font-semibold text-destructive' : 'font-semibold'}>{formatMoney(fila.saldo)}</span>
    ) },
    { key: 'movimientos', header: 'Movimientos', headerClassName: 'text-right', className: 'text-right', cell: (fila) => fila.movimientos },
    { key: 'active', header: '', cell: (fila) => (fila.active ? null : <Badge tone="neutral">Inactiva</Badge>) }
  ];

  const columnasMovimientos: Column<Movimiento>[] = [
    { key: 'booked_at', header: 'Fecha', cell: (fila) => fila.booked_at },
    { key: 'account_alias', header: 'Cuenta', cell: (fila) => fila.account_alias },
    { key: 'concept', header: 'Concepto', cell: (fila) => (
      <div>
        <p>{fila.concept}</p>
        <p className="text-[11px] text-muted-foreground">
          {fila.supplier_name ?? fila.customer_name ?? ''}{fila.reference ? ` · ${fila.reference}` : ''}
        </p>
      </div>
    ) },
    { key: 'amount', header: 'Importe', headerClassName: 'text-right', className: 'text-right', cell: (fila) => (
      <span className={Number(fila.amount) < 0 ? 'text-destructive' : 'text-emerald-600'}>{formatMoney(Number(fila.amount))}</span>
    ) },
    { key: 'acciones', header: '', cell: (fila) => (puedeEditar && fila.source === 'manual' ? (
      <Button variant="ghost" size="sm" onClick={() => void borrarMovimiento(fila.id)} title="Borrar movimiento">
        <Trash2 className="size-3.5" />
      </Button>
    ) : null) }
  ];

  const columnasFormas: Column<FormaPago>[] = [
    { key: 'code', header: 'Código', cell: (fila) => <span className="font-mono text-xs">{fila.code}</span> },
    { key: 'name', header: 'Forma de pago', cell: (fila) => (
      <div>
        <p>{fila.name}</p>
        <p className="text-[11px] text-muted-foreground">
          {TIPOS.find((tipo) => tipo.value === fila.kind)?.label ?? fila.kind}
          {fila.appliesTo === 'ambos' ? ' · cobros y pagos' : fila.appliesTo === 'cobros' ? ' · solo cobros' : ' · solo pagos'}
        </p>
      </div>
    ) },
    { key: 'days', header: 'Plazo', headerClassName: 'text-right', className: 'text-right', cell: (fila) => (fila.days ? `${fila.days} días` : 'Al contado') },
    { key: 'cuenta', header: 'Cuenta', cell: (fila) => listaCuentas.find((cuenta) => cuenta.id === fila.bankAccountId)?.alias ?? '—' },
    { key: 'active', header: '', cell: (fila) => (fila.active ? null : <Badge tone="neutral">Inactiva</Badge>) }
  ];

  if (resumen.isError) return <ErrorState message="No se pudo cargar la tesorería" onRetry={() => void resumen.refetch()} />;

  return (
    <div className="space-y-4">
      {dialogoConfirmacion}
      <PageHeader
        title="Tesorería"
        description="Cuentas de la empresa, formas de pago y el dinero que entra y sale."
        actions={puedeEditar ? (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setFormMov({ ...movVacio, accountId: activas[0]?.id ?? '' }); }}>
              <Banknote className="mr-1.5 size-4" /> Movimiento
            </Button>
            <Button onClick={() => { setFormCuenta(cuentaVacia); setRellenado({ bankName: '', swift: '', entidad: '' }); setEditandoCuenta(null); }}>
              <Plus className="mr-1.5 size-4" /> Cuenta
            </Button>
          </div>
        ) : null}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Saldo en cuentas" value={formatMoney(resumen.data?.saldoTotal ?? 0)} hint={activas.length === 1 ? '1 cuenta activa' : `${activas.length} cuentas activas`} icon={Landmark} />
        <StatCard label="Pendiente de pago" value={formatMoney(resumen.data?.pendientePago.total ?? 0)} hint={resumen.data?.pendientePago.facturas === 1 ? '1 factura' : `${resumen.data?.pendientePago.facturas ?? 0} facturas`} icon={CreditCard} tone="warning" />
        <StatCard label="Facturas vencidas" value={String(resumen.data?.pendientePago.vencidas ?? 0)} hint="Con fecha de vencimiento pasada" icon={Building2} tone={(resumen.data?.pendientePago.vencidas ?? 0) > 0 ? 'warning' : 'primary'} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="resumen">Resumen</TabsTrigger>
          <TabsTrigger value="cuentas">Cuentas</TabsTrigger>
          <TabsTrigger value="formas">Formas de pago</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'resumen' ? (
        <Card className="p-0">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Últimos movimientos</h3>
          </div>
          {resumen.isLoading
            ? <p className="px-3 py-3 text-xs text-muted-foreground">Cargando movimientos…</p>
            : (resumen.data?.movimientos.length ?? 0) === 0
              ? <EmptyState title="Sin movimientos" description="Registra un cobro o un pago para empezar." icon={Banknote} />
              : <DataTable columns={columnasMovimientos} rows={resumen.data?.movimientos ?? []} rowKey={(fila) => fila.id} />}
        </Card>
      ) : null}

      {tab === 'cuentas' ? (
        <>
          {formCuenta ? (
            <Card className="space-y-3 p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {editandoCuenta ? 'Editar cuenta' : 'Nueva cuenta'}
              </h3>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Nombre corto" required><Input value={formCuenta.alias} onChange={(e) => setFormCuenta({ ...formCuenta, alias: e.target.value })} /></Field>
                <Field label="IBAN" required hint="Se valida el dígito de control; al escribirlo se rellenan el banco y el SWIFT"><Input value={formCuenta.iban}
                  onChange={(e) => {
                    // La decisión vive en `aplicarIban`: misma entidad no toca nada, otra entidad
                    // sustituye solo lo autocompletado, y un IBAN completo sin banco lo vacía.
                    const resultado = aplicarIban(formCuenta, rellenado, e.target.value);
                    setFormCuenta({ ...formCuenta, ...resultado.cuenta });
                    setRellenado(resultado.rellenado);
                  }} /></Field>
                <Field label="Banco"><Input value={formCuenta.bankName} onChange={(e) => setFormCuenta({ ...formCuenta, bankName: e.target.value })} /></Field>
                <Field label="Saldo inicial"><Input value={formCuenta.openingBalance} onChange={(e) => setFormCuenta({ ...formCuenta, openingBalance: e.target.value })} /></Field>
                <Field label="Moneda"><Input value={formCuenta.currency} onChange={(e) => setFormCuenta({ ...formCuenta, currency: e.target.value.toUpperCase() })} /></Field>
                <Field label="SWIFT / BIC"><Input value={formCuenta.swift} onChange={(e) => setFormCuenta({ ...formCuenta, swift: e.target.value.toUpperCase() })} /></Field>
              </div>
              <div className="flex flex-wrap gap-4 text-xs">
                <label className="flex items-center gap-2"><input type="checkbox" checked={formCuenta.isDefaultCollections} onChange={(e) => setFormCuenta({ ...formCuenta, isDefaultCollections: e.target.checked })} /> Cuenta por defecto para cobros</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formCuenta.isDefaultPayments} onChange={(e) => setFormCuenta({ ...formCuenta, isDefaultPayments: e.target.checked })} /> Cuenta por defecto para pagos</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formCuenta.active} onChange={(e) => setFormCuenta({ ...formCuenta, active: e.target.checked })} /> Activa</label>
              </div>
              <Field label="Notas"><Textarea rows={2} value={formCuenta.notes} onChange={(e) => setFormCuenta({ ...formCuenta, notes: e.target.value })} /></Field>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => { setFormCuenta(null); setEditandoCuenta(null); }}>Cancelar</Button>
                <Button disabled={guardando || !formCuenta.alias || !formCuenta.iban} onClick={() => void guardarCuenta()}>Guardar</Button>
              </div>
            </Card>
          ) : null}
          {cuentas.isError ? <ErrorState message="No se pudieron cargar las cuentas" onRetry={() => void cuentas.refetch()} /> : null}
          <Card className="p-0">
            <DataTable
              loading={cuentas.isLoading}
              columns={columnasCuentas}
              rows={listaCuentas}
              rowKey={(fila) => fila.id}
              onRowClick={puedeEditar ? ((fila) => {
                setEditandoCuenta(fila.id);
                // Lo guardado cuenta como «ya rellenado», con la entidad que lo originó (vacía si el
                // banco no está en la tabla, para no borrar lo escrito a mano).
                setRellenado(rellenadoDeGuardado({ iban: fila.iban, bankName: fila.bankName, swift: fila.swift ?? null }));
                setFormCuenta({
                  alias: fila.alias, bankName: fila.bankName ?? '', iban: fila.iban,
                  swift: fila.swift ?? '', currency: fila.currency,
                  openingBalance: String(fila.openingBalance), isDefaultCollections: fila.isDefaultCollections,
                  isDefaultPayments: fila.isDefaultPayments, notes: fila.notes ?? '', active: fila.active
                });
              }) : undefined}
            />
          </Card>
        </>
      ) : null}

      {tab === 'formas' ? (
        <>
          {formForma ? (
            <Card className="space-y-3 p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {editandoForma ? 'Editar forma de pago' : 'Nueva forma de pago'}
              </h3>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Código" required hint="Mayúsculas, sin espacios"><Input value={formForma.code} disabled={Boolean(editandoForma)} onChange={(e) => setFormForma({ ...formForma, code: e.target.value.toUpperCase() })} /></Field>
                <Field label="Nombre" required><Input value={formForma.name} onChange={(e) => setFormForma({ ...formForma, name: e.target.value })} /></Field>
                <Field label="Tipo">
                  <Select value={formForma.kind} onChange={(e) => setFormForma({ ...formForma, kind: e.target.value })}>
                    {TIPOS.map((tipo) => <option key={tipo.value} value={tipo.value}>{tipo.label}</option>)}
                  </Select>
                </Field>
                <Field label="Se usa para">
                  <Select value={formForma.appliesTo} onChange={(e) => setFormForma({ ...formForma, appliesTo: e.target.value as FormaPago['appliesTo'] })}>
                    <option value="ambos">Cobros y pagos</option>
                    <option value="cobros">Solo cobros (clientes)</option>
                    <option value="pagos">Solo pagos (proveedores)</option>
                  </Select>
                </Field>
                <Field label="Plazo en días"><Input value={formForma.days} onChange={(e) => setFormForma({ ...formForma, days: e.target.value })} /></Field>
                <Field label="Cuenta de la empresa">
                  <Select value={formForma.bankAccountId} onChange={(e) => setFormForma({ ...formForma, bankAccountId: e.target.value })}>
                    <option value="">Sin cuenta concreta</option>
                    {listaCuentas.map((cuenta) => <option key={cuenta.id} value={cuenta.id}>{cuenta.alias}</option>)}
                  </Select>
                </Field>
              </div>
              <div className="flex flex-wrap gap-4 text-xs">
                <label className="flex items-center gap-2"><input type="checkbox" checked={formForma.requiresIban} onChange={(e) => setFormForma({ ...formForma, requiresIban: e.target.checked })} /> Exige IBAN del cliente</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formForma.active} onChange={(e) => setFormForma({ ...formForma, active: e.target.checked })} /> Activa</label>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => { setFormForma(null); setEditandoForma(null); }}>Cancelar</Button>
                <Button disabled={guardando || !formForma.code || !formForma.name} onClick={() => void guardarForma()}>Guardar</Button>
              </div>
            </Card>
          ) : puedeEditar ? (
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => { setFormForma(formaVacia); setEditandoForma(null); }}>
                <Plus className="mr-1.5 size-4" /> Forma de pago
              </Button>
            </div>
          ) : null}
          {formas.isError ? <ErrorState message="No se pudieron cargar las formas de pago" onRetry={() => void formas.refetch()} /> : null}
          <Card className="p-0">
            <DataTable loading={formas.isLoading} columns={columnasFormas} rows={listaFormas} rowKey={(fila) => fila.id}
              onRowClick={puedeEditar ? ((fila) => {
                setEditandoForma(fila.id);
                setFormForma({
                  code: fila.code, name: fila.name, kind: fila.kind, appliesTo: fila.appliesTo,
                  days: String(fila.days), requiresIban: fila.requiresIban,
                  bankAccountId: fila.bankAccountId ?? '', sortOrder: String(fila.sortOrder), active: fila.active
                });
              }) : undefined}
            />
          </Card>
        </>
      ) : null}

      {formMov ? (
        <Card className="space-y-3 p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nuevo movimiento de cuenta</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Cuenta" required>
              <Select value={formMov.accountId} onChange={(e) => setFormMov({ ...formMov, accountId: e.target.value })}>
                <option value="">Elige la cuenta</option>
                {activas.map((cuenta) => <option key={cuenta.id} value={cuenta.id}>{cuenta.alias}</option>)}
              </Select>
            </Field>
            <Field label="Importe" required hint="Negativo si sale dinero"><Input value={formMov.amount} onChange={(e) => setFormMov({ ...formMov, amount: e.target.value })} /></Field>
            <Field label="Fecha" required><Input type="date" value={formMov.bookedAt} onChange={(e) => setFormMov({ ...formMov, bookedAt: e.target.value })} /></Field>
            <Field label="Concepto" required className="sm:col-span-2"><Input value={formMov.concept} onChange={(e) => setFormMov({ ...formMov, concept: e.target.value })} /></Field>
            <Field label="Forma de pago">
              <Select value={formMov.methodId} onChange={(e) => setFormMov({ ...formMov, methodId: e.target.value })}>
                <option value="">Sin especificar</option>
                {listaFormas.filter((forma) => forma.active).map((forma) => <option key={forma.id} value={forma.id}>{forma.name}</option>)}
              </Select>
            </Field>
            <Field label="Referencia"><Input value={formMov.reference} onChange={(e) => setFormMov({ ...formMov, reference: e.target.value })} /></Field>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setFormMov(null)}>Cancelar</Button>
            <Button disabled={guardando || !formMov.accountId || !formMov.amount || !formMov.concept} onClick={() => void guardarMovimiento()}>Registrar</Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
